import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const themeTable = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "unset",
          minHeight: "300px",
          "& .MuiDataGrid-main": {
            backgroundColor: "#fff",
            border: "unset",
          },
          "& .MuiDataGrid-cell": {
            border: "unset",
            minHeight: "33px",
            fontSize: "15px",
            borderBottom: "0.5px solid #ECEAEA",
            height: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          },
          "& .MuiDataGrid-row": {
            color: "#000",
            cursor: "pointer",
            minHeight: "33px !important",
            maxHeight: "33px !important",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#1DBDCD4F",
            minHeight: "50px !important",
          },
          "& .MuiDataGrid-container--top [role=row]": {
            backgroundColor: "#00AEC3",
            borderRadius: "50px",
          },
          "& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader": {
            borderBottom: "unset",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "unset",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#00AEC3",
            fontFamily: "Encode Sans",
            color: "#fff",
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "18px",
            textAlign: "center",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "unset",
          },
          "& .Mui-selected": {
            backgroundColor: "#1DBDCD4F !important",
          },
          "& .MuiDataGrid-filler": {
            "--rowBorderColor:": "unset !important",
          },
        },
      },
    },
  },
});

// Buscamos la clave mapping_key dentro del arreglo de permissions de cada usuario
const checkUserPermission = (userItem, requiredPermissions) => {
  if (!userItem) return false;

  const userPermissions = userItem.permissions || userItem.permission || [];
  const permissionsList = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

  if (Array.isArray(userPermissions)) {
    return userPermissions.some((p) => {
      const userKey = typeof p === "string" ? p : (p?.mapping_key || p?.value || p?.name);
      return permissionsList.includes(userKey);
    });
  }

  return false;
};

function AssignTicketComponent(props) {
  const {
    users,
    handleOnSubmit,
    ticketSelected,
    ticketData,
    showAssignTicket,
    setShowAssignTicket,
  } = props;

  const [userSelected, setUserSelected] = useState("");
  const [showConfirmSendAssignment, setShowConfirmSendAssignment] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);

  const columns = [
    {
      field: "full_name",
      headerName: "Agente",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="swt-table-field-name">{params.value}</div>
      ),
    },
    {
      field: "details",
      headerName: "Asignados",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => params.value?.cant_claims_assigned || 0,
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
        <p className="swt-manager-filter-empty-title">
          No se encontraron agentes habilitados.
        </p>
      </Box>
    );
  }

  useEffect(() => {
    if (users && users.length > 0) {
      const permisosRequeridos = [
        "gestion_tickets",
        "manage_assigned_tickets",
        "escalate_tickets"
      ];

      const filtered = users.filter((userItem) => {
        // Nos quedamos con los users que tengan los permisos requeridos
        const hasPerm = checkUserPermission(userItem, permisosRequeridos);
        // Volvemos a filtrar y quitamos quienes no tengan sub-rol, o si llega vacío
        const hasSubRole = Boolean(userItem.sub_role);
        const matchesSearch = userItem.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
        return hasPerm && hasSubRole && matchesSearch;
      });

      setRows(filtered);
    } else {
      setRows([]);
    }
  }, [users, searchTerm]);

  return (
    <>
      <Dialog
        open={showAssignTicket}
        onClose={() => setShowAssignTicket(!showAssignTicket)}
        sx={{
          "&.MuiDialog-root .MuiDialog-paper": {
            padding: "20px",
            borderRadius: "35px",
            maxWidth: "100%",
            width: "900px",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Encode Sans", fontSize: "22px", fontWeight: "600", pb: 1 }}>
          Gestión de tickets
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", background: "#ECEAEA", borderRadius: "5px", p: "20px", mb: "15px", mt: 1 }}>
            <Typography
              sx={{
                fontFamily: "Encode Sans",
                fontSize: "17px",
                fontWeight: "200",
                width: "auto",
              }}
            >
              Asignar agente a ticket:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Encode Sans",
                fontSize: "17px",
                fontWeight: "400",
                width: "auto",
                p: "10px 12px",
                backgroundColor: "#E81F76",
                color: "#fff",
                borderRadius: "5px",
                ml: "10px",
              }}
            >
              Nro: <span style={{ fontWeight: "500" }}>{ticketSelected}</span>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "200" }}>
              Selecciona un agente
            </Typography>
            <TextField
              placeholder="Buscar agente..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: "260px" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#E81F76" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "20px", backgroundColor: "#fff" }
              }}
            />
          </Box>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ maxHeight: 320, borderRadius: "8px", border: "1px solid #ECEAEA", overflowX: "hidden" }}
          >
            <Table stickyHeader aria-label="tabla de agentes">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#00AEC3", color: "#fff", fontWeight: 600, fontSize: "15px" }}>
                    Nombre y Apellido
                  </TableCell>
                  {/* <TableCell align="center" sx={{ backgroundColor: "#00AEC3", color: "#fff", fontWeight: 600, fontSize: "15px" }}>
                    Administrador
                  </TableCell> */}
                  <TableCell align="center" sx={{ backgroundColor: "#00AEC3", color: "#fff", fontWeight: 600, fontSize: "15px" }}>
                    Reclamos asignados
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((user) => {
                    const userId = user.uuid || user.id;
                    const isSelected = userSelected === userId;
                    return (
                      <TableRow
                        key={userId}
                        onClick={() => setUserSelected(userId)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isSelected ? "#1DBDCD4F" : "inherit",
                          "&:hover": { backgroundColor: "#1DBDCD25" },
                        }}
                      >
                        <TableCell sx={{ fontSize: "14px", py: "12px", fontWeight: isSelected ? 600 : 400 }}>
                          {user.full_name}
                        </TableCell>
                        {/* <TableCell align="center" sx={{ fontSize: "14px", py: "12px" }}>
                          {user.sub_role || "-"}
                        </TableCell> */}
                        <TableCell align="center" sx={{ fontSize: "14px", py: "12px" }}>
                          {user.details?.cant_claims_assigned || 0}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#888", fontSize: "14px" }}>
                      No se encontraron agentes habilitados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            sx={{
              borderRadius: "50px",
              backgroundColor: "#fff",
              color: "#000",
              border: "1px solid #ccc",
              width: "140px",
              padding: "8px 20px",
              fontWeight: "600",
              textTransform: "none",
            }}
            onClick={() => {
              setUserSelected("");
              setShowAssignTicket(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            sx={{
              borderRadius: "50px",
              backgroundColor: "#E81F76",
              color: "#fff",
              padding: "8px 30px",
              fontWeight: "600",
              textTransform: "none",
              "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" }
            }}
            disabled={!userSelected}
            onClick={() => {
              setShowAssignTicket(false);
              setShowConfirmSendAssignment(true);
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showConfirmSendAssignment}
        onClose={() => setShowConfirmSendAssignment(false)}
        sx={{
          "&.MuiDialog-root .MuiDialog-paper": {
            padding: "20px",
            borderRadius: "30px",
            width: "600px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "20px", fontWeight: "600" }}>
          ¿Desea confirmar la asignación de este ticket?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "16px", color: "#666", mt: 1 }}>
            En caso de no querer confirmar la asignación, haga clic en “Cancelar”.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            sx={{
              borderRadius: "50px",
              backgroundColor: "#fff",
              color: "#000",
              border: "1px solid #838383",
              padding: "8px 25px",
              textTransform: "none",
            }}
            onClick={() => {
              setShowConfirmSendAssignment(false);
              setShowAssignTicket(true);
            }}
          >
            Cancelar
          </Button>
          <Button
            sx={{
              borderRadius: "50px",
              backgroundColor: "#00AEC3",
              color: "#fff",
              padding: "8px 25px",
              textTransform: "none",
              boxShadow: "0px 4px 4px rgba(0,0,0,0.25)"
            }}
            onClick={() => handleOnSubmit(userSelected)}
          >
            Asignar ticket
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AssignTicketComponent;